import { Component, OnInit, TemplateRef, ViewChild,OnDestroy } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { WorkflowContextService, WorkflowService } from 'src/app/services';
import { CurrentActivityData, DataTableJanusData } from './data-table-janus.model';
import { CommonConfirmationModalData } from 'src/app/components/common/common-confirmation-modal/common-confirmation-modal.component.model';
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  tap
} from 'rxjs';
import { WorkflowApplicationsJanusService } from '../workflow-applications-janus.service';
import { svgs } from 'src/assets/export.assets';
import { SortDirection } from '@angular/material/sort';
import { MatMenuTrigger } from '@angular/material/menu';
@Component({
  selector: 'app-data',
  standalone: false,
  templateUrl: './data-table-janus.component.html',
  styleUrl: './data-table-janus.component.scss'
})
export class DataTableJanusComponent implements OnInit {
  private readonly storedSortBy = 'stored_janus_workflow_applications_sortBy';
  private readonly storedSortByApiKey =
    'stored_janus_workflow_applications_sortByApiKey';
  private readonly storedSortMode =
    'stored_janus_workflow_applications_sortMode';
  private readonly unreadChatColor = '#B3261E';
  private readonly readChatColor = '#2196F3';
  private readonly defaultChatColor = '#5F6368';
  private readonly unreadNotesColor = '#B3261E';
  private readonly readNotesColor = '#2196F3';
  private readonly defaultNotesColor = '#5F6368';
  private readonly unreadAttachmentColor = '#B3261E';
  private readonly readAttachmentColor = '#2196F3';
  private readonly defaultAttachmentColor = '#5F6368';

  private readonly HIDE_SHOW_COLUMNS_KEY_PREFIX = 'janus_';
  // Added by amisha on 2026-06-03 to centralize the Janus columns that staff users must always keep visible.
  // Reason: App # now follows the same locked-column behavior as App Date and Status in both the API payload and the column settings menu.
  private readonly NON_HIDEABLE_COLUMN_API_KEYS = [
    'ApplicationSubmittedOn',
    'APPLICATION_STATUS'
  ];
  // Added by amisha on 2026-06-01 to keep Janus aligned with the shared menu's maximum visible-column rule.
  // Reason: saved and initial column selections must also clamp to 7, not only future checkbox clicks.
  private readonly MAX_VISIBLE_COLUMNS = 7;
  errorIcon = svgs.errorIcon.name;
  // Start Code added by Sanchit on 08/04/2026
  signOffDatatableIcon = svgs.signOffDatatableIcon.name;
  feesDatatableIcon = svgs.feesDatatableIcon.name;
  inspectionDatatableIcon = svgs.inspectionDatatableIcon.name;
  // End Code added by Sanchit on 08/04/2026
  // Added by amisha on 2026-06-01 to reuse the shared confirmation modal for Janus column-limit warnings.
  // Reason: the 8th-column message should follow the app's standard modal pattern instead of using a toaster.
  columnLimitModalData: CommonConfirmationModalData = {
    open: false,
    modalDescp: 'Column Limit Reached',
    modalSubDescp: 'You can only show 7 columns at once.',
    okBtn: true,
    closeBtn: false,
    yesNoBtn: false,
    status: 'info'
  };
  totalRecords = 0;
  dataTableDetails: DataTableJanusData = {
    departmentId: '',
    pageNumber: 1,
    maxRows: 10,
    headers: [],
    rows: [],
    fetchingHeadersAndRows: false,
    fetchingRows: false,
    fetchError: '',
    selectedIds: [],
    filterInputs: [],
    reRun: true,
    expandedRow: null,
    expandedRowId: null,
    sortBy: null,
    sortMode: '',
    sortByApiKey: null,
    columnsToDisplayed: [],
    columnsToInclude: undefined,
    // Added by amisha on 2026-06-03 to always request the Janus required columns even when saved user preferences omit them.
    // Reason: older localStorage selections can hide App # unless these non-hideable API keys are forced into every fetch.
    fixedColumnsToInclude: [
      ...this.NON_HIDEABLE_COLUMN_API_KEYS,
      'DocumentsToReview',
      'messages',
      'TOTAL_FEES_DUE_NUM',
      'TOTAL_FEES_PAID_NUM',
      'TOTAL_FEES_NUM',
      'ApplicationNotesCount',
      'UnreadNotesCount',
      'AssignedSignOffsCount',
      'ApprovedSignOffsCount',
      'DisapprovedSignOffsCount',
      'InspectionsCount',
      'AssignedInspectionsCount',
      'ApprovedInspectionsCount',
      'DisapprovedInspectionsCount',
      'ALL_MESSAGES',
      'CreateDate',
      'PARCELID',
      'TotalDocumentCount',
      // Added by Amisha on 2026-09-07.
      // Reason: staff chat mail needs these values even when App Type or Site Address is hidden in the table.
      'WORKFLOW_NAME',
      'PARCEL_ADDRESS'
    ],
    fetchSpecificDataBasedOnId: false,
    totalNoOfPages: 1,
    showRelateToPermitView: false,
    showOnlyAttachments: false,
    showOnlyNotes: false
  };

  dataTableKeys = {
    appDate: 'App Date',
    status: 'Status',
    appNo:'App #'
  };
  // Added by amisha on 2026-02-10 to store the shared Janus chat window state on the staff applications table.
  // Reason: chat actions from row icons need a local open/close flag and row context without coupling that UI state to the table dataset itself.
  showChat = false;
  // Added by Amisha on 2026-09-09.
  // Reason: notify the shared chat to highlight when staff attempts to open a different application chat.
  chatAttentionTrigger = 0;
  private shouldRefreshChatTableOnClose = false;
  chatContext: {
    applicationId: string;
    applicationType: string;
    // Added by Amisha on 2026-07-02.
    // Reason: chat mail content needs the department label even though the chat API itself does not provide it.
    departmentName: string;
    siteAddress: string;
    internalMessageId?: string;
  } | null = null;

  @ViewChild('attachmentsTemplate', { static: true })
  attachmentsTemplate!: TemplateRef<any>;

  @ViewChild('chatTemplate', { static: true })
  chatTemplate!: TemplateRef<any>;

  @ViewChild('notesTemplate', { static: true })
  notesTemplate!: TemplateRef<any>;

  @ViewChild('signOffTemplate', { static: true })
  signOffTemplate!: TemplateRef<any>;

  @ViewChild('feesTemplate', { static: true })
  feesTemplate!: TemplateRef<any>;

  @ViewChild('inspectionsTemplate', { static: true })
  inspectionsTemplate!: TemplateRef<any>;

  @ViewChild('expansionTemplate', { static: true })
  expansionTemplate!: TemplateRef<any>;

  @ViewChild('applicationNoTemplate', { static: true })
  applicationNoTemplate!: TemplateRef<any>;

  constructor(
    private route: ActivatedRoute,
    private workflowSvc: WorkflowService,
    private router: Router,
    private workflowApplicationsJanusSvc: WorkflowApplicationsJanusService,
    public context: WorkflowContextService
  ) {}
  private readonly PAGE_SIZE_STORAGE_KEY =
    'wm-common-table-page-size-application';
  ngOnInit(): void {
    const storedPageSize = localStorage.getItem(this.PAGE_SIZE_STORAGE_KEY);

    if (storedPageSize) {
      const pageSize = Number(storedPageSize);

      if ([10, 25, 100, 200].includes(pageSize)) {
        this.dataTableDetails.maxRows = pageSize;
      }
    }

    const isNavigationStackNull =
      !this.router.getCurrentNavigation() &&
      this.route.snapshot.queryParamMap.has('id') &&
      !this.route.snapshot.queryParamMap.has('s');
    // null on browser refresh/direct URL
    if (isNavigationStackNull) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { s: 'external' },
        queryParamsHandling: 'merge',
        replaceUrl: true
      });
    }
    this.workflowApplicationsJanusSvc.data$.subscribe(data => {
      if (data.scrollToExpandedRow === true) {
        setTimeout(() => {
          this.scrollClickedRowToTop();
          this.workflowApplicationsJanusSvc.setData({
            ...this.workflowApplicationsJanusSvc.getData(),
            scrollToExpandedRow: false
          });
        }, 500);
      }
      if (data.showRelateToPermitView === true) {
        this.dataTableDetails.showRelateToPermitView =
          data.showRelateToPermitView;
      } else {
        this.dataTableDetails.showRelateToPermitView =
          data.showRelateToPermitView;
      }
    });
    // Refresh the parent grid after an activity is processed inside the expanded
    // application. Keep the existing rows in place while the request runs so the
    // inline application does not have to be closed before fee, sign-off, or
    // inspection status icons reflect the latest server values.
    // this.workflowApplicationsJanusSvc.refreshTable$.subscribe(() => {
    //   if (!this.dataTableDetails.departmentId) return;

    //   this.fetchApplicationsBasedOnDeparment(
    //     this.dataTableDetails.departmentId,
    //     { preserveRows: true }
    //   );
    // });
    
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        let currentRoute = this.route;
        // Traverse to deepest child
        while (currentRoute.firstChild) {
          currentRoute = currentRoute.firstChild;
        }
        const applicationId = currentRoute.snapshot.paramMap.get(
          'applicationId'
        );
        if (applicationId) {
          this.context.applicationViewType$.emit('overlay');
        } else {
          this.context.applicationViewType$.emit('none');
        }
      });
    this.route.queryParamMap.subscribe(queryParam => {
      // Added by amisha on 2026-08-26.
      // Reason: keep staff chat state synchronized when chat query parameters
      // are opened through browser navigation or a copied URL.
      this.openChatFromQueryParams();

      const source = queryParam.get('s');
      if (isNavigationStackNull || (source && source === 'external')) {
        this.dataTableDetails.fetchSpecificDataBasedOnId = true;
      } else {
        const paramPageNo = queryParam.get('p');
        if (paramPageNo) {
          this.dataTableDetails.pageNumber = Number(paramPageNo);
        } else {
          this.dataTableDetails.pageNumber = 1;
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { p: 1 },
            queryParamsHandling: 'merge',
            replaceUrl: true
          });
        }
      }

      const paramApplicationId = queryParam.get('id');
      if (paramApplicationId) {
        this.dataTableDetails.expandedRowId = paramApplicationId;
        if (this.dataTableDetails.rows.length > 0) {
          this.dataTableDetails.expandedRow =
            this.dataTableDetails.rows.find(
              row => row.id === this.dataTableDetails.expandedRowId
            ) || null;
          if (this.dataTableDetails.expandedRow === null) {
            const currentPage = this.route.snapshot.queryParamMap.get('p');
            this.router.navigate([], {
              relativeTo: this.route,
              queryParams: {
                p: currentPage
              },
              queryParamsHandling: 'replace'
            });
          }
        }
      } else {
        // no source external
        if (source && source === 'external') {
          return;
        }
        if (this.dataTableDetails.fetchSpecificDataBasedOnId === true) {
          this.applyStepperHeight();
          this.dataTableDetails.fetchSpecificDataBasedOnId = false;
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { p: 1 },
            queryParamsHandling: 'replace'
          });
          this.fetchApplicationsBasedOnDeparment(
            this.dataTableDetails.departmentId
          );
        }

        this.dataTableDetails.expandedRowId = null;
        this.dataTableDetails.expandedRow = null;
        this.context.applicationId$.emit(null);
        this.context.isTestApplication$.emit(null);
      }
    });

    const departmentId$ = this.route.parent!.parent!.paramMap.pipe(
      map(params => params.get('departmentId')),
      filter((id): id is string => !!id),
      distinctUntilChanged(),
      tap(() => {
        this.dataTableDetails.reRun = true;
      })
    );

    combineLatest([
      departmentId$,
      this.workflowApplicationsJanusSvc.data$,
      this.workflowApplicationsJanusSvc.bulkSelection$
    ]).subscribe(([departmentId, data, selections]) => {
      this.dataTableDetails.selectedIds = selections;
      if (data.goToFirstPage === true) {
        this.dataTableDetails.pageNumber = 1;
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { p: this.dataTableDetails.pageNumber },
          queryParamsHandling: 'replace'
        });
      }

      this.dataTableDetails.departmentId = departmentId;
      const storageKey = `${this.HIDE_SHOW_COLUMNS_KEY_PREFIX}${departmentId}`;
      const storedColumns = localStorage.getItem(storageKey);

      if (storedColumns) {
        try {
          this.dataTableDetails.columnsToInclude = JSON.parse(storedColumns);
        } catch (error) {
          this.dataTableDetails.columnsToInclude = undefined;
        }
      } else {
        this.dataTableDetails.columnsToInclude = undefined;
      }

      this.dataTableDetails.filterInputs = [
        ...(data.filterInputs?.flatMap(f => f.options) || []),
        ...(data.attributeFilterInputs?.flatMap(f => f.options) || []),
        ...(data.searchText?.flatMap(f => f.options) || [])
      ];
      const storedSortByApiKey = localStorage.getItem(this.storedSortByApiKey);
      if (
        storedSortByApiKey &&
        this.dataTableDetails.columnsToInclude?.includes(storedSortByApiKey)
      ) {
        this.dataTableDetails.sortBy = localStorage.getItem(this.storedSortBy);
        this.dataTableDetails.sortByApiKey = storedSortByApiKey;
        this.dataTableDetails.sortMode = localStorage.getItem(
          this.storedSortMode
        ) as SortDirection;
      } else {
        localStorage.removeItem(this.storedSortBy);
        localStorage.removeItem(this.storedSortByApiKey);
        localStorage.removeItem(this.storedSortMode);
      }

      if (data.reRunJanusApplicationDataTable === true) {
        this.dataTableDetails.reRun = data.reRunJanusApplicationDataTable;
      }

      if (this.dataTableDetails.reRun) {
        this.fetchApplicationsBasedOnDeparment(departmentId);
      }
    });
  }

  onApplicationNumberClick = (event: MouseEvent, row: any) => {
    event.stopPropagation();

    setTimeout(() => {
      this.scrollClickedRowToTop();
    }, 500);
  };

  // Added by Amisha on 07-04-2026 to silently rerun the Janus applications API after chat send without clearing rows, so the chat icon updates without a visible table reload.
  fetchApplicationsBasedOnDeparment = (
    deptId: string,
    options?: { preserveRows?: boolean }
  ) => {
    const preserveRows =
      options?.preserveRows === true && this.dataTableDetails.rows.length > 0;

    this.dataTableDetails.fetchError = '';
    if (!preserveRows) {
      this.dataTableDetails.rows = [];
    }

    if (this.dataTableDetails.headers.length === 0 && !preserveRows) {
      this.dataTableDetails.fetchingHeadersAndRows = true;
    } else {
      this.dataTableDetails.fetchingRows = true;
    }
    this.workflowSvc
      .getDepartmentApplications(
        deptId,
        {
          inputValues:
            this.dataTableDetails.fetchSpecificDataBasedOnId === true
              ? [
                  {
                    title: 'application_id',
                    filterText: this.dataTableDetails.expandedRowId,
                    strOperator: 'is',
                    searchText: null,
                    selected: true
                  }
                ]
              : this.dataTableDetails.filterInputs,
          pageNumber:
            this.dataTableDetails.fetchSpecificDataBasedOnId === true
              ? 1
              : this.dataTableDetails.pageNumber,
          maxRows: this.dataTableDetails.maxRows
        },
        this.dataTableDetails.columnsToInclude
          ? [
              ...this.dataTableDetails.columnsToInclude,
              ...this.dataTableDetails.fixedColumnsToInclude
            ]
          : undefined,
        this.dataTableDetails.sortMode === 'desc'
          ? true
          : this.dataTableDetails.sortMode === 'asc'
          ? false
          : this.dataTableDetails.sortMode === ''
          ? undefined
          : undefined,
        this.dataTableDetails.sortByApiKey || undefined
      )
      .pipe(
        finalize(() => {
          this.dataTableDetails.fetchingHeadersAndRows = false;
          this.dataTableDetails.fetchingRows = false;
          this.dataTableDetails.reRun = false;
          this.workflowApplicationsJanusSvc.setData({
            ...this.workflowApplicationsJanusSvc.getData(),
            reRunJanusApplicationDataTable: false,
            goToFirstPage: false,
            //updated by amisha on 08/20/2026 for all the columns to show in atrribute filter
            visibleAttributeColumns: this.dataTableDetails.headers
              .filter(
                header =>
                  !!header.apiKey &&
                  ![
                    'documentsToReview',
                    'messages',
                    'ApplicationNotesCount',
                    'AssignedSignOffsCount',
                    'TOTAL_FEES_DUE_NUM',
                    'InspectionsCount',
                    'TOTAL_FEES_NUM',
                    'TOTAL_FEES_PAID_NUM',
                    'AssignedInspectionsCount',
                    'ApprovedInspectionsCount',
                    'DisapprovedInspectionsCount'
                  ].includes(header.apiKey)
              )
              .map(header => ({
                id: header.apiKey as string,
                name: header.label || header.key
              }))
          });
        })
      )
      .subscribe({
        next: response => {
          // Updated by amisha on 2026-06-03 to read Janus header definitions from the published dataset config.
          // Reason: the current staff applications response exposes the active field list there, so App # locking and column visibility must use that source.
          if (!response?.dataSet?.publishedDataSetConfig?.fields) {
            this.dataTableDetails.fetchError = 'some error occured';
            return;
          }

          const fetchedHeaders = response.dataSet.publishedDataSetConfig.fields;

          this.dataTableDetails.headers = fetchedHeaders
            .filter(field => !(field.isAvailableColumn === false))
            .map(field => ({
              key: field.resultHeader,
              label: field.resultHeader,
              // Added by amisha on 2026-08-24 to assign stable widths to every dynamic Janus data column.
              // Reason: changing server-side sort results must not change the table's column geometry.
              width: this.getStableColumnWidth(field.resultHeader, field.name),
              headerType: 'text',
              cellType:
                field.name === 'APPLICATION_NUMBER' || field.name === 'CURRENT_ACTIVITY_JSON' ? 'template' : 'text',
              cellTemplate:
                field.name === 'APPLICATION_NUMBER'
                  ? this.applicationNoTemplate
                  : field.name === 'CURRENT_ACTIVITY_JSON'
                    ? this.currentActivityTemplate
                    : null,
              expandable: true,
              apiKey: field.name,
              hideable: !this.NON_HIDEABLE_COLUMN_API_KEYS.includes(field.name),
              hidden:
                this.dataTableDetails.columnsToInclude !== undefined
                  ? this.dataTableDetails.columnsToInclude.includes(
                      field.name
                    ) === true
                    ? false
                    : true
                  : false,
              cursor: 'pointer'
            }));
          // Added by amisha on 2026-06-01 to normalize persisted Janus selections before rendering.
          // Reason: older saved states may contain too many columns and must be clamped to the 7-column limit.
          this.dataTableDetails.columnsToInclude = this.getLimitedColumnsToInclude(
            this.dataTableDetails.headers
          );

          const storageKey = `${this.HIDE_SHOW_COLUMNS_KEY_PREFIX}${this.dataTableDetails.departmentId}`;
          if (this.dataTableDetails.columnsToInclude === undefined) {
            localStorage.removeItem(storageKey);
          } else {
            localStorage.setItem(
              storageKey,
              JSON.stringify(this.dataTableDetails.columnsToInclude)
            );
          }

          this.dataTableDetails.headers = this.dataTableDetails.headers.map(
            header => ({
              ...header,
              hidden:
                this.dataTableDetails.columnsToInclude !== undefined
                  ? !this.dataTableDetails.columnsToInclude.includes(
                      header.apiKey
                    )
                  : false
            })
          );

          // Added by amisha on 2026-06-01 to rebuild the visible Janus column keys from the normalized API-key selection.
          // Reason: menu-close saves must reflect the actual current header list rather than an outdated hard-coded map.
          const selectableDisplayedColumns =
            this.dataTableDetails.columnsToInclude !== undefined
              ? this.dataTableDetails.headers
                  .filter(header =>
                    this.dataTableDetails.columnsToInclude?.includes(
                      header.apiKey
                    )
                  )
                  .map(header => header.key)
              : this.dataTableDetails.headers.map(header => header.key);

          this.dataTableDetails.headers = [
            {
              key: 'selected',
              headerType: 'checkbox',
              cellType: 'checkbox',
              sortable: false,
              hideMenu: true,
              width: '30px'
            },
            ...this.dataTableDetails.headers,
            {
              key: 'documentsToReview',
              label: 'Attachments',
              apiKey: 'documentsToReview',
              headerType: 'icon',
              cellType: 'template',
              icon: 'attach_file',
              cellTemplate: this.attachmentsTemplate,
              hideMenu: true,
              width: '60px'
            },
            {
              key: 'messages',
              label: 'Chat',
              apiKey: 'messages',
              headerType: 'icon',
              cellType: 'template',
              icon: 'chat_bubble_outline', // Code edited by Sanchit on 08/04/2026
              cellTemplate: this.chatTemplate,
              hideMenu: true,
              width: '60px'
            },
            {
              key: 'notes',
              label: 'Notes',
              apiKey: 'ApplicationNotesCount',
              headerType: 'icon',
              cellType: 'template',
              icon: 'note_alt',
              cellTemplate: this.notesTemplate,
              hideMenu: true,
              width: '60px'
            },
            {
              key: 'signOff',
              label: 'Sign Off',
              apiKey: 'AssignedSignOffsCount',
              headerType: 'icon',
              cellType: 'template',
              icon: svgs.signOffDatatableIcon.name, // Code edited by Sanchit on 08/04/2026
              cellTemplate: this.signOffTemplate,
              hideMenu: true,
              width: '60px'
            },
            {
              key: 'fees',
              label: 'Fees',
              apiKey: 'TOTAL_FEES_DUE_NUM',
              headerType: 'icon',
              cellType: 'template',
              icon: svgs.feesDatatableIcon.name, // Code edited by Sanchit on 08/04/2026
              cellTemplate: this.feesTemplate,
              hideMenu: true,
              width: '60px'
            },
            {
              key: 'inspections',
              label: 'Inspections',
              apiKey: 'InspectionsCount',
              headerType: 'icon',
              cellType: 'template',
              icon: svgs.inspectionDatatableIcon.name, // Code edited by Sanchit on 08/04/2026
              cellTemplate: this.inspectionsTemplate,
              hideMenu: true,
              width: '60px'
            }
          ];
          this.dataTableDetails.columnsToDisplayed = [
            'selected',
            ...selectableDisplayedColumns,
            'documentsToReview',
            'messages',
            'notes',
            'signOff',
            'fees',
            'inspections'
          ];
          const normalize = (val: string) =>
            val
              ?.toLowerCase()
              .replace(/\s+/g, '')
              .trim();

          this.totalRecords = response.dataSetResult.totalApplicationCount;

          this.dataTableDetails.totalNoOfPages = Math.ceil(
            response.dataSetResult.totalApplicationCount /
              this.dataTableDetails.maxRows
          );

          this.dataTableDetails.rows = response.dataSetResult.rows.map(row => {
            const normalizedValues: any = {};

            Object.keys(row.values).forEach(key => {
              normalizedValues[normalize(key)] = row.values[key];
            });

            const newRow: any = {
              id: row.id,
              selected: this.dataTableDetails.selectedIds.includes(row.id),
              // Look these up through the normalized key bag so casing/spacing drift on the
              // server payload (e.g. "App Date" vs "app Date", "Status" vs "status") doesn't
              // silently wipe row['App Date']/row['Status']. Downstream icon color guards
              // (getSignOffColor / getFeesColor / getInspectionsColor) treat an empty App Date
              // as "not submitted" and render N/A for all three columns, so an exact-match
              // lookup here would cascade into losing the SignOff, Fees, and Inspections
              // columns on every row.
              Status: normalizedValues[normalize('status')] ?? '',
              'App Date':
                normalizedValues[normalize('app date')] ??
                normalizedValues[normalize('appdate')] ??
                '',
              // Flag from the server (GetDepartmentApplications) indicating the current user is a
              // member of a Role responsible for one of the application's current activities. Used
              // by the App # cell template to render the number in red with a red badge. Comes back
              // as a regular global application field value ("1" / "0") on row.values.
              userIsResponsibleForACurrentActivity:
                normalizedValues[
                  normalize('USER_IS_RESPONSIBLE_FOR_A_CURRENT_ACTIVITY')
                ] === '1'
            };

            this.dataTableDetails.headers.forEach(header => {
              if (header.key === 'selected') return; // skip checkbox column

              const rawValue = normalizedValues[normalize(header.key)] ?? '';

              const isCurrentActivity =
              this.isCurrentActivityField({
                name: header.apiKey,
                resultHeader: header.key
              });

                  if (isCurrentActivity) {
                    const activities = this.getCurrentActivities(rawValue);
                    newRow.currentActivities = activities;
                    newRow[header.key] = activities
  .map(activity => activity.activityName)
  .join(', ');
                  } else {
                    newRow[header.key] = this.formatValue(header.key, rawValue);
                  }
            });

            const unreadMessages = Number(
              normalizedValues[normalize('messages')]
            );
            const totalMessages = Number(
              normalizedValues[normalize('all messages')] ??
                normalizedValues[normalize('all_messages')]
            );
            const unreadAttachments = Number(
              normalizedValues[normalize('documentsToReview')]
            );
            const totalAttachments = Number(
              normalizedValues[normalize('total document count')] ??
                normalizedValues[normalize('TotalDocumentCount')]
            );
            const unreadNotes = Number(
              normalizedValues[normalize('unread notes count')] ??
                normalizedValues[normalize('unreadnotecount')] ??
                normalizedValues[normalize('unread_notes_count')] ??
                normalizedValues[normalize('unread notes')] ??
                normalizedValues[normalize('unreadnotescount')]
            );
            const totalNotes = Number(
              normalizedValues[normalize('notes')] ??
                normalizedValues[normalize('application notes count')] ??
                normalizedValues[normalize('applicationnotescount')]
            );

            newRow.messages = !isNaN(unreadMessages)
              ? unreadMessages.toString()
              : '0';
            newRow.allMessages = !isNaN(totalMessages) ? totalMessages : 0;
            newRow.documentsToReview = !isNaN(unreadAttachments)
              ? unreadAttachments.toString()
              : '0';
            newRow.totalAttachments = !isNaN(totalAttachments)
              ? totalAttachments
              : 0;
            newRow.unreadNotes = !isNaN(unreadNotes)
              ? unreadNotes
              : Number(newRow.notes ?? 0) || 0;
            newRow.totalNotes = !isNaN(totalNotes)
              ? totalNotes
              : Number(newRow.notes ?? 0) || 0;
            // SIGNOFF
            const assigned = Number(
              normalizedValues[normalize('assigned SignOffs')]
            );
            const approved = Number(
              normalizedValues[normalize('approved SignOffs')]
            );
            const disapproved = Number(
              normalizedValues[normalize('disapproved SignOffs')]
            );
            // Start Code edited by Sanchit on 08/21/2026
            if (!isNaN(disapproved) && disapproved > 0) {
              newRow.signOff = {
                color: 'red',
                tooltip: 'Rejected'
              };
            } else if (!isNaN(approved) && approved > 0) {
              newRow.signOff = {
                color: 'green',
                tooltip: 'Completed'
              };
            } else if (!isNaN(assigned) && assigned > 0) {
              newRow.signOff = {
                color: 'orange',
                tooltip: 'Action Needed'
              };
            } else {
              newRow.signOff = {
                color: 'grey',
                tooltip: 'Not Applicable'
              };
            }
            // End Code edited by Sanchit on 08/21/2026

            // Fees
            // Server may emit these under camelCase display names (e.g. 'feesDue')
            // or under the raw global-field constant names (e.g. 'TOTAL_FEES_DUE_NUM')
            // depending on whether the department dataset was hand-configured or
            // filled in by BackfillDepartmentApplicationStaticFields on the server.
            const DueFees = Number(
              normalizedValues[normalize('feesDue')] ??
                normalizedValues[normalize('TOTAL_FEES_DUE_NUM')]
            );

            const PaidFees = Number(
              normalizedValues[normalize('paidFees')] ??
                normalizedValues[normalize('TOTAL_FEES_PAID_NUM')]
            );
            const TotalFees = Number(
              normalizedValues[normalize('totalFees')] ??
                normalizedValues[normalize('TOTAL_FEES_NUM')]
            );
            const Feesvalues = [DueFees, PaidFees, TotalFees];
            const FeesallUndefined = Feesvalues.every(v => isNaN(v));
            const FeesanyGreaterThanZero = Feesvalues.some(
              v => !isNaN(v) && v > 0
            );
            const FeesallZero = Feesvalues.every(v => !isNaN(v) && v === 0);
            // Start Code edited by Sanchit on 08/21/2026
            if (FeesallUndefined) {
              newRow.fees = {
                color: 'grey',
                tooltip: 'Not Applicable'
              };
            } else if (!isNaN(DueFees) && DueFees > 0) {
              newRow.fees = {
                color: 'orange',
                tooltip: 'Action Needed'
              };
            } else if (
              !isNaN(TotalFees) &&
              !isNaN(PaidFees) &&
              TotalFees > 0 &&
              PaidFees >= TotalFees
            ) {
              newRow.fees = {
                color: 'green',
                tooltip: 'Completed'
              };
            } else {
              newRow.fees = {
                color: 'grey',
                tooltip: 'Not Applicable'
              };
            }
            // End Code edited by Sanchit on 08/21/2026

            // Inspections
            // Same dual-key story as Fees: display name from hand-configured
            // datasets vs. raw constant name from the server-side backfill.
            const AssignedInsp = Number(
              normalizedValues[normalize('assigned Inspections')] ??
                normalizedValues[normalize('AssignedInspectionsCount')]
            );

            const ApprovedInsp = Number(
              normalizedValues[normalize('approved Inspections')] ??
                normalizedValues[normalize('ApprovedInspectionsCount')]
            );

            const DisapprovedInsp = Number(
              normalizedValues[normalize('disapproved Inspections')] ??
                normalizedValues[normalize('DisapprovedInspectionsCount')]
            );

            // // Start Code edited by Sanchit on 08/21/2026
            // if (!isNaN(DisapprovedInsp) && DisapprovedInsp > 0) {
            //   newRow.inspections = {
            //     color: 'red',
            //     tooltip: 'Rejected'
            //   };
            // } else if (!isNaN(ApprovedInsp) && ApprovedInsp > 0) {
            //   newRow.inspections = {
            //     color: 'green',
            //     tooltip: 'Completed'
            //   };
            // } else {
            //   newRow.inspections = {
            //     color: 'grey',
            //     tooltip: 'Not Applicable'
            //   };
            // }
            // // End Code edited by Sanchit on 08/21/2026

            // Start Code edited by Sanchit on 09/03/2026
if (!isNaN(DisapprovedInsp) && DisapprovedInsp > 0) {
  newRow.inspections = {
    color: 'red',
    tooltip: 'Rejected'
  };
} else if (!isNaN(ApprovedInsp) && ApprovedInsp > 0) {
  newRow.inspections = {
    color: 'green',
    tooltip: 'Completed'
  };
} else if (!isNaN(AssignedInsp) && AssignedInsp > 0) {
  newRow.inspections = {
    color: 'orange',
    tooltip: 'Action Needed'
  };
} else {
  newRow.inspections = {
    color: 'grey',
    tooltip: 'Not Applicable'
  };
}
// End Code edited by Sanchit on 09/03/2026

            return newRow;
          });
          // Added by amisha on 2026-08-26.
          // Reason: a direct chat URL can only be resolved after its application
          // row and the mail context fields have finished loading.
          this.openChatFromQueryParams();

          if (this.dataTableDetails.expandedRowId) {
            this.applyStepperHeight();

            this.dataTableDetails.expandedRow =
              this.dataTableDetails.rows.find(
                row => row.id === this.dataTableDetails.expandedRowId
              ) || null;
            setTimeout(() => {
              this.scrollClickedRowToTop();
            }, 500);
            if (this.dataTableDetails.expandedRow === null) {
              if (this.dataTableDetails.fetchSpecificDataBasedOnId === true) {
                // after re-run we still did not find application then clear all query params just keep page
                const currentPage = this.route.snapshot.queryParamMap.get('p');
                this.router.navigate([], {
                  relativeTo: this.route,
                  queryParams: {
                    p: currentPage
                  },
                  queryParamsHandling: 'replace'
                });
              } else {
                this.dataTableDetails.fetchSpecificDataBasedOnId = true;
                this.router.navigate([], {
                  relativeTo: this.route,
                  queryParams: {
                    s: 'external',
                    p: null
                  },
                  queryParamsHandling: 'merge'
                });
                this.fetchApplicationsBasedOnDeparment(
                  this.dataTableDetails.departmentId
                );
              }
            }
          }
        },
        error: _error => {
          this.dataTableDetails.fetchError = 'some error occured';
          return;
        }
      });
  };
  onSortChanged(event: {
    sortField: string | null;
    sortDescending: boolean | null;
  }): void {
    // App Type true
    this.dataTableDetails.sortBy = event.sortField;
    this.dataTableDetails.sortByApiKey =
      this.dataTableDetails.headers.find(
        header => header.key === event.sortField
      )?.apiKey || null;
    if (this.dataTableDetails.sortByApiKey) {
      this.dataTableDetails.sortMode =
        event.sortDescending === true
          ? 'desc'
          : event.sortDescending === false
          ? 'asc'
          : event.sortDescending === null
          ? ''
          : '';
      this.dataTableDetails.pageNumber = 1;
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { p: this.dataTableDetails.pageNumber },
        queryParamsHandling: 'replace'
      });
      // stored in local storage
      localStorage.setItem(this.storedSortBy, this.dataTableDetails.sortBy);
      localStorage.setItem(
        this.storedSortByApiKey,
        this.dataTableDetails.sortByApiKey
      );
      localStorage.setItem(this.storedSortMode, this.dataTableDetails.sortMode);

      this.fetchApplicationsBasedOnDeparment(
        this.dataTableDetails.departmentId
      );
    }
  }
  // Added by amisha on 2026-06-01 to clamp Janus column persistence to the screen's 7-column maximum.
  // Reason: initial load and restored localStorage state should never reopen with more columns than allowed.
  private getLimitedColumnsToInclude(
    headers: Array<{ apiKey?: string; hideable?: boolean }>
  ): string[] | undefined {
    const availableApiKeys = headers
      .map(header => header.apiKey)
      .filter((apiKey): apiKey is string => !!apiKey);

    if (!availableApiKeys.length) {
      return undefined;
    }

    const requiredApiKeys = headers
      .filter(header => header.hideable === false && !!header.apiKey)
      .map(header => header.apiKey as string);
    const requestedApiKeys =
      this.dataTableDetails.columnsToInclude !== undefined
        ? this.dataTableDetails.columnsToInclude.filter(apiKey =>
            availableApiKeys.includes(apiKey)
          )
        : availableApiKeys;
    const limitedApiKeys: string[] = [];

    requiredApiKeys.forEach(apiKey => {
      if (!limitedApiKeys.includes(apiKey)) {
        limitedApiKeys.push(apiKey);
      }
    });

    requestedApiKeys.forEach(apiKey => {
      if (
        !limitedApiKeys.includes(apiKey) &&
        limitedApiKeys.length < this.MAX_VISIBLE_COLUMNS
      ) {
        limitedApiKeys.push(apiKey);
      }
    });

    const areAllColumnsSelected =
      limitedApiKeys.length === availableApiKeys.length &&
      availableApiKeys.every(apiKey => limitedApiKeys.includes(apiKey));

    return areAllColumnsSelected ? undefined : limitedApiKeys;
  }
  // Added by amisha on 2026-06-01 to open the shared Janus warning modal when the visible-column cap is exceeded.
  // Reason: the column-settings menu now emits the warning so the screen can render it with the common confirmation modal.
  onVisibleColumnLimitReached(message: string): void {
    this.columnLimitModalData = {
      ...this.columnLimitModalData,
      open: true,
      modalSubDescp: message
    };
  }
  toggleColumnsHideShow(displayedColumns: string[]): void {
    // Added by amisha on 2026-06-01 to derive Janus persistence from the live header metadata.
    // Reason: hard-coded label-to-key mapping was missing newer columns and caused incorrect re-checking on menu close.
    const alwaysVisibleKeys = new Set([
      'selected',
      'documentsToReview',
      'messages',
      'notes',
      'signOff',
      'fees',
      'inspections'
    ]);

    const hideShowColumnKeys = this.dataTableDetails.headers
      .filter(header => !alwaysVisibleKeys.has(header.key))
      .filter(header => displayedColumns.includes(header.key))
      .map(header => header.apiKey)
      .filter((apiKey): apiKey is string => !!apiKey);
    // Start Code edited by Sanchit on 08/26/2026
    // this.dataTableDetails.columnsToInclude = hideShowColumnKeys;
    const storageKey = `${this.HIDE_SHOW_COLUMNS_KEY_PREFIX}${this.dataTableDetails.departmentId}`;

    const allColumnKeys = this.dataTableDetails.headers
      .filter(header => !alwaysVisibleKeys.has(header.key))
      .map(header => header.apiKey)
      .filter((apiKey): apiKey is string => !!apiKey);

    const areAllColumnsSelected =
      hideShowColumnKeys.length === allColumnKeys.length &&
      allColumnKeys.every(key => hideShowColumnKeys.includes(key));

    const newColumnsToInclude = areAllColumnsSelected
      ? undefined
      : hideShowColumnKeys;

    // Compare with the current selection before updating/fetching.
    const currentColumnsToInclude = this.dataTableDetails.columnsToInclude;

    const columnsChanged =
      (currentColumnsToInclude === undefined) !==
        (newColumnsToInclude === undefined) ||
      (currentColumnsToInclude !== undefined &&
        newColumnsToInclude !== undefined &&
        (currentColumnsToInclude.length !== newColumnsToInclude.length ||
          currentColumnsToInclude.some(
            key => !newColumnsToInclude.includes(key)
          )));

    // Nothing changed — menu was only opened/closed.
    if (!columnsChanged) {
      return;
    }

    this.dataTableDetails.columnsToInclude = newColumnsToInclude;

    if (newColumnsToInclude === undefined) {
      localStorage.removeItem(storageKey);
      // End Code edited by Sanchit on 08/26/2026
    } else {
      localStorage.setItem(storageKey, JSON.stringify(newColumnsToInclude));
    }
    this.fetchApplicationsBasedOnDeparment(this.dataTableDetails.departmentId);
  }
  setExpandedRow = (expandedRow: any | null) => {
    if (expandedRow === null) {
      // incase source he then source is getting removed anyways in route
      this.context.applicationViewType$.emit('none');
      this.context.applicationId$.emit(null);
      this.context.isTestApplication$.emit(null);
      const currentPage = this.route.snapshot.queryParamMap.get('p');
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          p: currentPage || 1
        },
        queryParamsHandling: 'replace'
      });
      this.dataTableDetails.expandedRowId = null;
      this.dataTableDetails.expandedRow = null;
    } else {
      this.context.applicationViewType$.emit('inline');
      this.dataTableDetails.expandedRowId = expandedRow.id;
      this.dataTableDetails.expandedRow = expandedRow;
      this.router
        .navigate([], {
          relativeTo: this.route,
          queryParams: {
            id: this.dataTableDetails.expandedRowId
          },
          queryParamsHandling: 'merge'
        })
        .then(async () => {
          await this.applyStepperHeight();
        });
      setTimeout(() => {
        this.scrollClickedRowToTop();
      }, 500);
    }
  };
  private async applyStepperHeight(): Promise<void> {
    try {
      const el1 = await this.waitForElement('.sidebar-child-stepper');

      el1.style.setProperty('max-height', '100%', 'important');

      const interval = setInterval(() => {
        const el2 = el1.querySelector('.wizard-steps') as HTMLElement;

        if (el2) {
          el2.style.setProperty('max-height', '100%', 'important');
          clearInterval(interval);
        }
      }, 200);
    } catch (err) {}
  }
  private waitForElement(selector: string): Promise<HTMLElement> {
    return new Promise((resolve, reject) => {
      const interval = setInterval(() => {
        const el = document.querySelector(selector) as HTMLElement;

        if (el) {
          clearInterval(interval);
          resolve(el);
        }
      }, 200);

      setTimeout(() => {
        clearInterval(interval);
        reject(`Element not found: ${selector}`);
      }, 10000);
    });
  }

  // Added by amisha on 2026-08-24 to centralize fixed widths for dynamic application fields.
  // Reason: known columns need appropriate stable widths and all other fields need a predictable fallback for truncation.
  private getStableColumnWidth(label: string, apiKey: string): string {
    const normalizedLabel = (label || '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');

    if (apiKey === 'APPLICATION_NUMBER' || normalizedLabel === 'app') {
      return '150px';
    }

    if (apiKey === 'WORKFLOW_NAME' || normalizedLabel === 'apptype') {
      return '200px';
    }

    if (normalizedLabel.includes('currentactivity')) {
      return '250px';
    }

    if (normalizedLabel.includes('date')) {
      return '175px';
    }

    if (normalizedLabel.includes('address')) {
      return '200px';
    }

    if (apiKey === 'APPLICATION_STATUS' || normalizedLabel === 'status') {
      return '140px';
    }

    return '180px';
  }
  private scrollClickedRowToTop(attempt = 0): void {
    const MAX_ATTEMPTS = 20;

    setTimeout(() => {
      const wrapper = document.querySelector<HTMLElement>('.table-wrapper');
      const activeRow = document.querySelector<HTMLElement>(
        'tr.expanded-active-row'
      );

      if (!wrapper || !activeRow) {
        if (attempt < MAX_ATTEMPTS) {
          this.scrollClickedRowToTop(attempt + 1);
        }
        return;
      }

      const wrapperRect = wrapper.getBoundingClientRect();
      const rowRect = activeRow.getBoundingClientRect();

      const offset = rowRect.top - wrapperRect.top + wrapper.scrollTop - 45;

      wrapper.scrollTo({
        top: offset,
        behavior: 'smooth'
      });
    }, 100);
  }

  getSignOffColor = (row: any) => {
    if (row[this.dataTableKeys.appDate] === '') {
      return 'grey';
    } else {
      return row.signOff?.color; // Code edited by Sanchit on 08/21/2026
    }
  };
  getFeesColor = (row: any) => {
    if (row[this.dataTableKeys.appDate] === '') {
      return 'grey';
    } else {
      return row.fees?.color; // Code edited by Sanchit on 08/21/2026
    }
  };
  getInspectionsColor = (row: any) => {
    if (row[this.dataTableKeys.appDate] === '') {
      return 'grey';
    } else {
      return row.inspections?.color; // Code edited by Sanchit on 08/21/2026
    }
  };
  getChatColor = (row: any) => {
    const unreadMessages = Number(row?.messages ?? 0);
    const totalMessages = Number(row?.allMessages ?? 0);

    if (unreadMessages > 0) {
      return this.unreadChatColor;
    }

    if (totalMessages > 0) {
      return this.readChatColor;
    }

    return this.defaultChatColor;
  };
  getAttachmentColor = (row: any) => {
    const unreadAttachments = Number(row?.documentsToReview ?? 0);
    const totalAttachments = Number(row?.totalAttachments ?? 0);

    if (unreadAttachments > 0) {
      return this.unreadAttachmentColor;
    }

    if (totalAttachments > 0) {
      return this.readAttachmentColor;
    }

    return this.defaultAttachmentColor;
  };
  getNotesColor = (row: any) => {
    const unreadNotes = Number(row?.unreadNotes ?? 0);
    const totalNotes = Number(row?.totalNotes ?? row?.notes ?? 0);

    if (unreadNotes > 0) {
      return this.unreadNotesColor;
    }

    if (totalNotes > 0) {
      return this.readNotesColor;
    }

    return this.defaultNotesColor;
  };
  onPageChange(page: number): void {
    this.dataTableDetails.pageNumber = page + 1;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { p: this.dataTableDetails.pageNumber },
      queryParamsHandling: 'replace'
    });
    // Keep the rendered body in place while the next page loads. On mobile and
    // tablet the table uses an auto-height layout, so clearing the rows first
    // collapses the body and leaves the refresh loader with no visible area.
    // This also matches the Renewals Janus paging behavior.
    this.fetchApplicationsBasedOnDeparment(
      this.dataTableDetails.departmentId,
      { preserveRows: true }
    );
  }

  onRowsPerPageChange(size: number): void {
    const pageSize = Number(size) || 10;

    this.dataTableDetails.maxRows = pageSize;

    localStorage.setItem(this.PAGE_SIZE_STORAGE_KEY, String(pageSize));
    this.dataTableDetails.pageNumber = 1;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { p: this.dataTableDetails.pageNumber },
      queryParamsHandling: 'replace'
    });
    this.fetchApplicationsBasedOnDeparment(
      this.dataTableDetails.departmentId,
      { preserveRows: true }
    );
  }

  handleIconClick(event: MouseEvent, row: any, icon: string) {
    event.stopPropagation();

    if (icon === 'chat') {
      // Added by Amisha on 2026-09-09.
      // Reason: preserve the active staff chat and its URL until it is closed, highlighting only for another application.
      if (this.showChat) {
        if (this.chatContext?.applicationId !== row?.id) {
          this.chatAttentionTrigger += 1;
        }
        return;
      }

      // Added by amisha on 2026-08-26.
      // Reason: expose the open staff chat in the URL using the same deep-link
      // contract as the applicant My Applications page.
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          tab: 'chat',
          applicationId: row.id
        },
        queryParamsHandling: 'merge'
      });
      this.openChatForRow(row);
    } else if (icon === 'attachment') {
      if (this.dataTableDetails.expandedRow === null) {
        if (row[this.dataTableKeys.appDate] === '') {
          // show only attachment category
          this.dataTableDetails.showOnlyAttachments = true;
        } else {
          this.dataTableDetails.showOnlyAttachments = false;
        }
        this.context.applicationViewType$.emit('inline');
        this.dataTableDetails.expandedRowId = row.id;
        this.dataTableDetails.expandedRow = row;
        this.router
          .navigate([], {
            relativeTo: this.route,
            queryParams: {
              c: 'attachments',
              id: row.id,
              showAppView: 0,
              onlyAttachments: this.dataTableDetails.showOnlyAttachments
                ? 1
                : null
            },
            queryParamsHandling: 'merge'
          })
          .then(async () => {
            await this.applyStepperHeight();
          });
      } else {
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: {
            c: 'attachments',
            showAppView: 0
          },
          queryParamsHandling: 'merge'
        });
      }
      setTimeout(() => {
        this.scrollClickedRowToTop();
      }, 500);
    } else if (icon === 'note') {
      if (this.dataTableDetails.expandedRow === null) {
        if (row[this.dataTableKeys.appDate] === '') {
          // show only attachment category
          this.dataTableDetails.showOnlyNotes = true;
        } else {
          this.dataTableDetails.showOnlyNotes = false;
        }
        this.context.applicationViewType$.emit('inline');
        this.dataTableDetails.expandedRowId = row.id;
        this.dataTableDetails.expandedRow = row;
        this.router
          .navigate([], {
            relativeTo: this.route,
            queryParams: {
              c: 'notes',
              id: row.id,
              showAppView: 0,
              onlyNotes: this.dataTableDetails.showOnlyNotes ? 1 : null
            },
            queryParamsHandling: 'merge'
          })
          .then(async () => {
            await this.applyStepperHeight();
          });
      } else {
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: {
            c: 'notes',
            showAppView: 0
          },
          queryParamsHandling: 'merge'
        });
      }
      setTimeout(() => {
        this.scrollClickedRowToTop();
      }, 500);
    } else if (icon === 'status') {
      return;
    }
  }
  onChatReadStatusChanged(event: {
    applicationId: string;
    isRead: boolean;
  }): void {
    const row = this.dataTableDetails.rows.find(
      r => r.id === event.applicationId
    );
    if (!row) return;

    // 🔥 when read → remove highlight
    if (event.isRead) {
      row.messages = '0';
    } else {
      row.messages = '1';
    }
    this.dataTableDetails.rows = [...this.dataTableDetails.rows];
  }

  onChatMessageSent(event: { applicationId: string }): void {
    if (!event?.applicationId) {
      return;
    }

    this.dataTableDetails.rows = this.dataTableDetails.rows.map(row =>
      row.id === event.applicationId
        ? {
            ...row,
            messages: '0',
            allMessages: Number(row?.allMessages ?? 0) + 1
          }
        : row
    );
    this.shouldRefreshChatTableOnClose = true;
  }

  async closeChat(): Promise<void> {
    this.showChat = false;
    // Added by Amisha on 2026-09-09.
    // Reason: ensure the next staff chat does not inherit the previous chat's attention request.
    this.chatAttentionTrigger = 0;
    this.chatContext = null;

    const shouldRefreshTable =
      this.shouldRefreshChatTableOnClose &&
      !!this.dataTableDetails.departmentId;
    this.shouldRefreshChatTableOnClose = false;

    try {
      // Added by amisha on 2026-08-26.
      // Reason: remove staff chat deep-link state before a post-message table
      // refresh so the closed chat cannot immediately reopen.
      await this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          tab: null,
          applicationId: null
        },
        queryParamsHandling: 'merge',
        replaceUrl: true
      });
    } finally {
      if (shouldRefreshTable) {
        this.fetchApplicationsBasedOnDeparment(
          this.dataTableDetails.departmentId,
          { preserveRows: true }
        );
      }
    }
  }

  // Added by amisha on 2026-08-26.
  // Reason: restore staff chat state from a copied URL after the matching row
  // becomes available in the current applications result.
  private openChatFromQueryParams(): void {
    const queryParams = this.route.snapshot.queryParamMap;
    const applicationId = queryParams.get('applicationId');
    const tab = queryParams.get('tab');

    if (tab !== 'chat' || !applicationId) {
      return;
    }

    const row = this.dataTableDetails.rows.find(
      item => item.id === applicationId
    );

    if (row) {
      this.openChatForRow(row);
    }
  }

  // Added by amisha on 2026-08-26.
  // Reason: centralize staff chat context creation for icon clicks and URL-based
  // restoration so both entry points use identical validation and mail data.
  private openChatForRow(row: any): void {
    const applicationId = row?.id;

    // Updated by Amisha on 2026-09-03.
    // Reason: chat must remain available regardless of application date or status; only a valid application ID is required.
    if (!applicationId) {
      return;
    }

    // Added by Amisha on 2026-09-09.
    // Reason: guard URL-restored and row-triggered flows from replacing an active chat with another application.
    if (this.showChat) {
      if (this.chatContext?.applicationId !== applicationId) {
        this.chatAttentionTrigger += 1;
      }
      return;
    }

    // Added by Amisha on 2026-09-03.
    // Reason: Janus dataset result headers can vary in casing, so resolve mail fields without exact-case assumptions.
    this.chatContext = {
      applicationId: row.id,
      applicationType: this.getRowString(
        row,
        'App Type',
        'Application Type'
      ),
      departmentName: this.getFirstNonEmptyString(
        this.getRowString(row, 'Department', 'Department Name'),
        this.context.staffDepartmentName
      ),
      siteAddress: this.getRowString(row, 'Site Address', 'Address')
    };
    this.showChat = true;
  }

  // Added by Amisha on 2026-09-03.
  // Reason: read existing Janus row values such as "app Type" and "site Address" case-insensitively.
  private getRowString(row: any, ...keys: string[]): string {
    const normalize = (value: string) =>
      value.toLowerCase().replace(/\s+/g, '');
    const normalizedKeys = keys.map(normalize);
    const matchingKey = Object.keys(row || {}).find(key =>
      normalizedKeys.includes(normalize(key))
    );

    return matchingKey ? this.getFirstNonEmptyString(row[matchingKey]) : '';
  }

  // Added by Amisha on 2026-09-16.
  // Reason: expose case-insensitive staff row values to the mobile application-details card
  // while supporting the different dataset header labels used by departments.
  getMobileRowValue(row: any, ...keys: string[]): string {
    return this.getRowString(row, ...keys);
  }

  // Added by Amisha on 2026-09-21.
  // Reason: hide the duplicate mobile application details and shortcut row when the
  // inline expansion is displaying the complete Application View.
  get isFullApplicationViewActive(): boolean {
    const queryParams = this.route.snapshot.queryParamMap;
    return queryParams.get('showAppView') === '1' || queryParams.get('isLinkedApp') === '1';
  }

  // Added by Amisha on 2026-09-03.
  // Reason: prevent blank dataset fields from overriding the selected-department fallback used by staff mail.
  private getFirstNonEmptyString(...values: unknown[]): string {
    const value = values.find(
      candidate => typeof candidate === 'string' && candidate.trim().length > 0
    );

    return typeof value === 'string' ? value.trim() : '';
  }

  onInlineNotesChanged(event: {
    applicationId: string;
    unreadNotes: number;
    totalNotes: number;
  }): void {
    if (!event?.applicationId) {
      return;
    }

    const rowIndex = this.dataTableDetails.rows.findIndex(
      row => row.id === event.applicationId
    );
    if (rowIndex < 0) {
      return;
    }

    const targetRow = this.dataTableDetails.rows[rowIndex];
    targetRow.unreadNotes = event.unreadNotes;
    targetRow.totalNotes = event.totalNotes;
    targetRow.notes = event.totalNotes.toString();
    this.dataTableDetails.rows = [...this.dataTableDetails.rows];

    if (this.dataTableDetails.expandedRowId === event.applicationId) {
      this.dataTableDetails.expandedRow = targetRow;
    }
  }
  onInlineAttachmentsChanged(event: {
    applicationId: string;
    unreadAttachments: number;
    totalAttachments: number;
  }): void {
    if (!event?.applicationId) {
      return;
    }

    const rowIndex = this.dataTableDetails.rows.findIndex(
      row => row.id === event.applicationId
    );
    if (rowIndex < 0) {
      return;
    }

    const targetRow = this.dataTableDetails.rows[rowIndex];
    targetRow.documentsToReview = event.unreadAttachments;
    targetRow.totalAttachments = event.totalAttachments;
    this.dataTableDetails.rows = [...this.dataTableDetails.rows];

    if (this.dataTableDetails.expandedRowId === event.applicationId) {
      this.dataTableDetails.expandedRow = targetRow;
    }
  }

  onSpecificRowCheckboxClick = row => {
    if (
      row.selected === true &&
      !this.dataTableDetails.selectedIds.includes(row.id)
    ) {
      this.dataTableDetails.selectedIds.push(row.id);
    } else if (
      row.selected === false &&
      this.dataTableDetails.selectedIds.includes(row.id)
    ) {
      this.dataTableDetails.selectedIds = this.dataTableDetails.selectedIds.filter(
        id => row.id !== id
      );
    }
    this.workflowApplicationsJanusSvc.setBulkSelection(
      this.dataTableDetails.selectedIds
    );
  };
  onHeaderCheckboxClick = (rows: any[]) => {
    rows.forEach(row => {
      if (
        row.selected === true &&
        !this.dataTableDetails.selectedIds.includes(row.id)
      ) {
        this.dataTableDetails.selectedIds.push(row.id);
      } else if (
        row.selected === false &&
        this.dataTableDetails.selectedIds.includes(row.id)
      ) {
        this.dataTableDetails.selectedIds = this.dataTableDetails.selectedIds.filter(
          id => id !== row.id
        );
      }
    });

    this.workflowApplicationsJanusSvc.setBulkSelection(
      this.dataTableDetails.selectedIds
    );
  };

  formatValue = (key: string, value: any): any => {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    if (value === 'true' || value === true) return 'Yes';
    if (value === 'false' || value === false) return 'No';

    const normalizedKey = key.toLowerCase().replace(/\s+/g, '');

    const dateFields = ['appdate', 'issuedate'];
    if (dateFields.includes(normalizedKey)) {
      return typeof value === 'string' ? value.split(' ')[0] : value;
    }

    return value;
  };

  @ViewChild('currentActivityTemplate', { static: true })
  currentActivityTemplate!: TemplateRef<any>;

  activeCurrentActivities: CurrentActivityData[] = [];
  activeCurrentActivityRow: any = null;

  private currentActivityMenuCloseTimer: ReturnType<typeof setTimeout> | null =
    null;

  private activeCurrentActivityTrigger: MatMenuTrigger | null = null;

  private isCurrentActivityTriggerHovered = false;

  private isCurrentActivityMenuHovered = false;
  private isCurrentActivityField(field: {
    name?: string;
    resultHeader?: string;
  }): boolean {
    const normalize = (value: string = '') =>
      value
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '');

    return (
      normalize(field.name).includes('currentactivity') ||
      normalize(field.resultHeader).includes('currentactivity')
    );
  }

private getCurrentActivities(value: any): CurrentActivityData[] {
  if (value === null || value === undefined || value === '') {
    return [];
  }

  // API is returning CURRENT_ACTIVITY_JSON as a JSON string
  if (typeof value === 'string') {
    const trimmedValue = value.trim();

    // Empty JSON array
    if (trimmedValue === '[]') {
      return [];
    }

    // Try parsing JSON
    try {
      const parsedValue = JSON.parse(trimmedValue);

      if (Array.isArray(parsedValue)) {
        return parsedValue
          .filter(activity => activity?.activityName && activity?.activityDataId)
          .map(activity => ({
            activityName: activity.activityName,
            activityDataId: activity.activityDataId,
            categoryName: activity.categoryName ?? null,
            categoryId: activity.categoryId ?? null
          }));
      }
    } catch {
      // Not JSON, so treat it as the old comma-separated format
    }

    // Backward compatibility for old format:
    // "Search Parcel Data, Review Application"
    // return trimmedValue
    //   .split(',')
    //   .map(activity => activity.trim())
    //   .filter(Boolean)
    //   .map(activity => ({
    //     activityName: activity,
    //     activityDataId: ''
    //   }));
  }

  // In case API already gives us an actual array
  if (Array.isArray(value)) {
    return value
      .filter(activity => activity?.activityName)
      .map(activity => ({
        activityName: activity.activityName,
        activityDataId: activity.activityDataId,
        categoryName: activity.categoryName ?? null,
        categoryId: activity.categoryId ?? null
      }));
  }

  return [];
}



  openCurrentActivityMenu(
    row: any,
    trigger: MatMenuTrigger
  ): void {
    const activities = row?.currentActivities ?? [];
    this.activeCurrentActivityRow = row;

    if (activities.length <= 1) {
      return;
    }

    this.isCurrentActivityTriggerHovered = true;

    this.cancelCurrentActivityMenuClose();

    this.activeCurrentActivities = activities;
    this.activeCurrentActivityTrigger = trigger;

    trigger.openMenu();
  }

  onCurrentActivityTriggerLeave(): void {
    this.isCurrentActivityTriggerHovered = false;

    this.scheduleCurrentActivityMenuClose();
  }

  onCurrentActivityMenuEnter(): void {
    this.isCurrentActivityMenuHovered = true;

    this.cancelCurrentActivityMenuClose();
  }

  onCurrentActivityMenuLeave(): void {
    this.isCurrentActivityMenuHovered = false;

    this.scheduleCurrentActivityMenuClose();
  }
  scheduleCurrentActivityMenuClose(): void {
    this.cancelCurrentActivityMenuClose();

    this.currentActivityMenuCloseTimer = setTimeout(() => {
      if (
        this.isCurrentActivityTriggerHovered ||
        this.isCurrentActivityMenuHovered
      ) {
        return;
      }

      this.activeCurrentActivityTrigger?.closeMenu();
      this.activeCurrentActivityTrigger = null;
      this.activeCurrentActivities = [];
      this.currentActivityMenuCloseTimer = null;
    }, 200);
  }
  cancelCurrentActivityMenuClose(): void {
    if (this.currentActivityMenuCloseTimer) {
      clearTimeout(this.currentActivityMenuCloseTimer);
      this.currentActivityMenuCloseTimer = null;
    }
  }

  ngOnDestroy(): void {
    this.cancelCurrentActivityMenuClose();
  }
getActivityNames(activities: CurrentActivityData[]): string {
  return activities?.map(activity => activity.activityName).join(', ') || '';
}
  onCurrentActivityClick = (
  event: MouseEvent,
  activity: CurrentActivityData,
  row:any
): void => {
  event.stopPropagation();
  if(!row['App Date']){
   this.context.applicationViewType$.emit('inline');
      this.dataTableDetails.expandedRowId = row.id;
      this.dataTableDetails.expandedRow = row;
      this.router
        .navigate([], {
          relativeTo: this.route,
          queryParams: {
            id: this.dataTableDetails.expandedRowId,
            activityDataId: activity.activityDataId,
            showAppView: 1
          },
          queryParamsHandling: 'merge'
        })
        .then(async () => {
          await this.applyStepperHeight();
        });
      setTimeout(() => {
        this.scrollClickedRowToTop();
      }, 500);
  }else{
    if(activity?.categoryId || activity?.categoryName){
      this.context.applicationViewType$.emit('inline');
      this.dataTableDetails.expandedRowId = row.id;
      this.dataTableDetails.expandedRow = row;
      this.router
        .navigate([], {
          relativeTo: this.route,
          queryParams: {
            id: this.dataTableDetails.expandedRowId,
            activityDataId: activity.activityDataId,
            c:activity?.categoryName.toLowerCase(),
            showAppView: 0
          },
          queryParamsHandling: 'merge'
        })
        .then(async () => {
          await this.applyStepperHeight();
        });
      setTimeout(() => {
        this.scrollClickedRowToTop();
      }, 500);
    }else{
      this.context.applicationViewType$.emit('inline');
      this.dataTableDetails.expandedRowId = row.id;
      this.dataTableDetails.expandedRow = row;
      this.router
        .navigate([], {
          relativeTo: this.route,
          queryParams: {
            id: this.dataTableDetails.expandedRowId,
            activityDataId: activity.activityDataId,
            showAppView: 1
          },
          queryParamsHandling: 'merge'
        })
        .then(async () => {
          await this.applyStepperHeight();
        });
      setTimeout(() => {
        this.scrollClickedRowToTop();
      }, 500);
    }
  }
  
};
}
